import { AuthGuard, PreventLoginGuard } from '@services/Logic/auth.guard';
import {
  Routes,
  RouterModule,
  Router,
  NavigationEnd,
  Event,
} from '@angular/router';
import { NgModule } from '@angular/core';
import { ResetPasswordComponent } from '@dialogs/profileRelatedDialogs/resetPassword/resetPassword.component';
import { resolveFeatures } from '@app/shared/modules/growthbook';
import { GrowthBookService } from './shared/modules/growthbook';
import { environment } from '../environments/environment';

const appRoutes: Routes = [
  {
    path: '',
    resolve: { features: resolveFeatures },
    children: [
      {
        path: 'udforsk',
        loadChildren: () =>
          import(
            './pages/staticPages/landingDashboardTO/landingDashboardTO.module'
          ).then((m) => m.LandingDashboardTOModule),
      },
      {
        path: 'gavekort',
        loadChildren: () =>
          import(
            './pages/staticPages/giftCertificate/giftCertificate.module'
          ).then((m) => m.GiftCertificateModule),
      },
      {
        path: 'gavekort/koeb',
        loadChildren: () =>
          import(
            './pages/staticPages/giftCertificate/giftCertificateShop/giftCertificateShop.module'
          ).then((m) => m.GiftCertificateShopModule),
      },
      {
        path: 'gavekort/koeb/success',
        loadChildren: () =>
          import(
            './pages/staticPages/giftCertificate/giftCertificateSuccessPage/giftCertificateSuccessPage.module'
          ).then((m) => m.GiftCertificateSuccessPageModule),
      },
      {
        path: 'hh/detaljer',
        loadChildren: () =>
          import('./pages/profile/profile.module').then((m) => m.ProfileModule),
      },
      {
        path: 'opret',
        loadComponent: () =>
          import(
            './dialogs/createTaskRelatedDialogs/create-task/create-task-new'
          ).then((c) => c.CreateTaskComponent),
      },
      {
        path: 'tilmeld',
        loadChildren: () =>
          import(
            './pages/staticPages/authenticationDedicatedPage/authenticationDedicatedPage.module'
          ).then((m) => m.AuthenticationDedicatedPageModule),
      },
      {
        path: 'inviter',
        redirectTo: 'tilmeld',
      },
      {
        path: 'skadegodtgoerelse',
        loadChildren: () =>
          import('./pages/staticPages/insuranceInfo/insuranceInfo.module').then(
            (m) => m.InsuranceInfoModule,
          ),
      },
      {
        path: 'skadegodtgoerelse/ansoeg',
        loadComponent: () =>
          import(
            './pages/staticPages/insurance-application/insurance-application.component'
          ).then((m) => m.InsuranceApplicationComponent),
        canActivate: [AuthGuard],
      },
      {
        path: 'beskeder',
        loadChildren: () =>
          import('./pages/messages/messages.module').then(
            (m) => m.MessagesModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'hjaelp',
        loadChildren: () =>
          import('./pages/staticPages/faq/faq.module').then((m) => m.FaqModule),
      },
      {
        path: 'opgaver',
        loadChildren: () =>
          import('./pages/home/home.module').then((m) => m.HomeModule),
      },
      {
        path: 'kontakt',
        loadComponent: () =>
          import(
            './pages/staticPages/supportTicketPage/supporTicketPage.component'
          ).then((m) => m.SupporTicketPageComponent),
      },
      {
        path: 'kontakt-chat',
        loadComponent: () =>
          import(
            './pages/staticPages/support-chat/support-chat.component'
          ).then((c) => c.SupportChatComponent),
      },
      {
        path: 'handyhander-niveauer',
        loadChildren: () =>
          import(
            './pages/staticPages/pinployeeLevelsInfo/pinployeeLevelsInfo.module'
          ).then((m) => m.PinployeeLevelsInfoModule),
      },
      {
        path: 'vaerktoejer',
        loadChildren: () =>
          import('./pages/staticPages/tools/Tools.module').then(
            (m) => m.ToolsModule,
          ),
      },
      {
        path: 'indstillinger',
        loadChildren: () =>
          import('./pages/settings/settings.module').then(
            (m) => m.SettingsModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'dac7',
        loadComponent: () =>
          import('./pages/staticPages/dac7/dac7.component').then(
            (m) => m.Dac7Component,
          ),
      },
      {
        path: 'login',
        redirectTo: '/?authDialog=true',
      },
      {
        path: 'tilmeld/?authDialog=true',
        redirectTo: '/udforsk',
      },
      {
        path: 'prisvejledning',
        loadChildren: () =>
          import('./pages/staticPages/budgetGuide/budgetGuide.module').then(
            (m) => m.BudgetGuideModule,
          ),
      },
      {
        path: 'retningslinjer',
        loadChildren: () =>
          import(
            './pages/staticPages/communityGuidelines/communityGuidelines.module'
          ).then((m) => m.CommunityGuidelinesModule),
      },
      {
        path: 'reset',
        component: ResetPasswordComponent,
        canActivate: [PreventLoginGuard],
      },
      {
        path: 'notification-option-settings/:notificationType/:userId',
        loadChildren: () =>
          import(
            './pages/staticPages/notif-sett-option/notif-sett-option.module'
          ).then((m) => m.NotifSettOptionModule),
      },
      {
        path: 'lokal-data',
        loadChildren: () =>
          import(
            './pages/staticPages/dataVisualizationPage/dataVisualization.module'
          ).then((m) => m.DataVisualizationModule),
      },
      {
        // User actions: flexible way to create one-time links to perform actions, that
        // work even if the user is not signed in. The user will also be signed in after
        path: 'link',
        loadChildren: () =>
          import('./pages/staticPages/userAction/userAction.module').then(
            (m) => m.UserActionModule,
          ),
      },
      {
        path: 'ny-have-platform',
        loadComponent: () =>
          import(
            './pages/staticPages/gardenPlatformEmailSignup/gardenPlatformEmailSignup'
          ).then((c) => c.GardenPlatformEmailSignup),
      },
      {
        path: 'konflikt',
        loadChildren: () =>
          import('./pages/conflictCase/conflict-case.module').then(
            (m) => m.ConflictCaseModule,
          ),
      },
      {
        path: 'cleady',
        redirectTo: '',
      },
      {
        path: 'mit-id',
        loadComponent: () =>
          import(
            './components/mit-idverification/mit-idverification.component'
          ).then((c) => c.MitIDVerificationComponent),
        canActivate: [AuthGuard],
      },
      {
        path: '404',
        loadChildren: () =>
          import('./components/error/error.module').then((m) => m.ErrorModule),
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'udforsk',
      },
      {
        path: '**',
        loadChildren: () =>
          import('./components/error/error.module').then((m) => m.ErrorModule),
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      initialNavigation: 'enabledBlocking',
      anchorScrolling: 'enabled',
      enableTracing: environment.env === 'development',
    }),
  ],
  exports: [RouterModule],
  declarations: [],
})
export class AppRoutingModule {
  constructor(
    private router: Router,
    private growthbook: GrowthBookService,
  ) {
    this.router.resetConfig(appRoutes);
    this.router.setUpLocationChangeListener();
    this.router.events.subscribe({
      next: (event: Event) => {
        if (event instanceof NavigationEnd) {
          this.growthbook.setURL(window.location.href);
          this.growthbook.updateAttributes({ url: window.location.href });
          this.growthbook.refreshFeatures();
        }
      },
      error: (err) => console.error(err),
    });
  }
}
